import { Info } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { observer } from 'mobx-react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { injectTSDI } from '../../../core/tsdi';
import { Api } from '../../../core/api';
import { Label } from '../../../components/label';
import { I18n } from '../../../core/i18n';
import { links } from '../../../core/router';
import { UserContext } from '../../../users/user';
import {
    DetailItem,
    formatEnumLabel,
    formatKeyLabel,
    formatMetricLabel,
    formatPropertyValue,
    getActivityTypeFromDetails,
    getDetailGroup,
    getMetricDisplay,
    getMetricIcon,
    getMetricIconTone,
    selectMetricItems,
    formatVisitDateRange,
    formatDateTimeValue,
    formatDateTimeLocal,
    isRangeKey
} from './utils';
import { MetricTile, SectionHeader } from './components';
import { GpxAnalysisPanel } from '../../../components/gpx-analysis/GpxAnalysisPanel';

interface FitnessActivityDetailsProps {
    fitnessActivityId: number;
    onChange?: () => void;
}

const WORKOUT_TYPE_FIELDS = [
    { key: 'workoutCategory', label: 'Workout Category' },
    { key: 'workoutTypeV2', label: 'Workout Type' },
    { key: 'rawWorkoutType', label: 'Raw Workout Type' }
];

const WORKOUT_TYPE_KEYS = new Set(
    WORKOUT_TYPE_FIELDS.map((field) => field.key)
);

const TILE_GRID =
    'mt-3 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]';

export const FitnessActivityDetails = observer(
    ({ fitnessActivityId, onChange }: FitnessActivityDetailsProps) => {
        const { fitnessActivityClient } = injectTSDI(Api);
        const i18n = injectTSDI(I18n);
        const navigate = useNavigate();
        const user = React.useContext(UserContext);

        const activityDetailsRequest = useWrapRequest(
            async (fitnessActivityId) => {
                const result =
                    await fitnessActivityClient.admin.activitySupportControllerGetActivity(
                        fitnessActivityId
                    );
                return result.data;
            },
            {
                deps: [fitnessActivityId]
            }
        );

        const backfillRequest = useCallback(
            async (fitnessActivityId: number) => {
                const result =
                    await fitnessActivityClient.admin.activitySupportControllerBackfillActivity(
                        fitnessActivityId
                    );
                if (onChange != null) {
                    onChange();
                }
                return result.data;
            },
            [fitnessActivityId]
        );

        const rejectRequest = useCallback(
            async (fitnessActivityId: number) => {
                const result =
                    await fitnessActivityClient.admin.activitySupportControllerRejectActivity(
                        fitnessActivityId
                    );
                if (onChange != null) {
                    onChange();
                }
                return result.data;
            },
            [fitnessActivityId]
        );

        const approveRequest = useCallback(
            async (fitnessActivityId: number) => {
                const result =
                    await fitnessActivityClient.admin.activitySupportControllerDeleteActivityRejection(
                        fitnessActivityId
                    );
                if (onChange != null) {
                    onChange();
                }
                return result.data;
            },
            [fitnessActivityId]
        );

        const showBackfillButton = useMemo(() => {
            const fitnessActivityDetails = activityDetailsRequest.$;
            if (
                fitnessActivityDetails &&
                fitnessActivityDetails.backfill?.backfillDateTime === null
            ) {
                const fitnessSources =
                    fitnessActivityDetails.additionalProperties.find(
                        (source) => source.key === 'sources'
                    );
                return fitnessSources
                    ? (fitnessSources.value as string[]).includes(
                          'com.garmin.apis'
                      )
                    : false;
            }
            return false;
        }, [activityDetailsRequest.$]);

        return (
            <>
                {activityDetailsRequest.match({
                    loading: () => (
                        <div className="flex flex-col gap-4">
                            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                                {[0, 1, 2].map((index) => (
                                    <Skeleton
                                        key={`summary-skeleton-${index}`}
                                        className="h-[50px]"
                                    />
                                ))}
                            </div>
                            <Skeleton className="h-4 w-[35%]" />
                            <div className={TILE_GRID}>
                                {Array.from({ length: 8 }).map((_, index) => (
                                    <Skeleton
                                        key={`metric-skeleton-${index}`}
                                        className="h-[68px]"
                                    />
                                ))}
                            </div>
                        </div>
                    ),
                    error: () => (
                        <Alert variant="destructive">
                            <AlertDescription>
                                Error loading activity details
                                {activityDetailsRequest.error?.message
                                    ? `: ${activityDetailsRequest.error.message}`
                                    : '.'}
                            </AlertDescription>
                        </Alert>
                    ),
                    fetched: (fitnessActivityDetails) => {
                        const reasonLabel =
                            fitnessActivityDetails.rejectionReason
                                ? formatEnumLabel(
                                      fitnessActivityDetails.rejectionReason
                                  )
                                : null;
                        const visibilityValue =
                            fitnessActivityDetails.hidden === true
                                ? 'Hidden'
                                : fitnessActivityDetails.hidden === false
                                  ? 'Visible'
                                  : 'Unknown';

                        const detailItems: DetailItem[] = [
                            {
                                key: 'createdDate',
                                label: formatKeyLabel('createdDate'),
                                value: formatPropertyValue(
                                    fitnessActivityDetails.createdDate
                                ),
                                rawValue: fitnessActivityDetails.createdDate,
                                group: 'Core Details'
                            },
                            {
                                key: 'modifiedDate',
                                label: formatKeyLabel('modifiedDate'),
                                value: formatPropertyValue(
                                    fitnessActivityDetails.modifiedDate
                                ),
                                rawValue: fitnessActivityDetails.modifiedDate,
                                group: 'Core Details'
                            },
                            {
                                key: 'activityPoints',
                                label: formatKeyLabel('activityPoints'),
                                value: formatPropertyValue(
                                    fitnessActivityDetails.activityPoints
                                ),
                                rawValue: fitnessActivityDetails.activityPoints,
                                group: 'Core Details'
                            }
                        ];

                        if (fitnessActivityDetails.rejectionReason !== null) {
                            detailItems.push({
                                key: 'rejectionReason',
                                label: formatKeyLabel('rejectionReason'),
                                value: reasonLabel ?? 'Unknown reason',
                                rawValue:
                                    fitnessActivityDetails.rejectionReason,
                                group: 'Core Details'
                            });
                        }

                        fitnessActivityDetails.additionalProperties
                            .filter(
                                (property) =>
                                    !WORKOUT_TYPE_KEYS.has(property.key)
                            )
                            .forEach((property) => {
                                detailItems.push({
                                    key: property.key,
                                    label: formatKeyLabel(
                                        property.key.replace(
                                            /^calculatedMetric\./,
                                            ''
                                        )
                                    ),
                                    value: formatPropertyValue(property.value),
                                    rawValue: property.value,
                                    group: getDetailGroup(property.key)
                                });
                            });

                        const workoutTypeTiles =
                            fitnessActivityDetails.type === 'WORKOUT'
                                ? WORKOUT_TYPE_FIELDS.map(({ key, label }) => {
                                      const property =
                                          fitnessActivityDetails.additionalProperties.find(
                                              (item) => item.key === key
                                          );
                                      const rawValue = property?.value;
                                      return {
                                          key,
                                          label,
                                          value:
                                              rawValue === null ||
                                              rawValue === undefined
                                                  ? '—'
                                                  : String(rawValue)
                                      };
                                  })
                                : [];

                        const groupedDetails = [
                            'Core Details',
                            'Activity Profile',
                            'Performance',
                            'Calculated Metrics',
                            'Source & Device',
                            'Location',
                            'Other'
                        ]
                            .map((group) => ({
                                group,
                                items: detailItems.filter(
                                    (item) => item.group === group
                                )
                            }))
                            .filter(({ items }) => items.length > 0);

                        const activityType =
                            getActivityTypeFromDetails(detailItems);
                        const metricCandidates = detailItems.filter(
                            (item) =>
                                item.group !== 'Core Details' &&
                                item.group !== 'Other'
                        );
                        const metricItems = selectMetricItems(
                            metricCandidates,
                            activityType,
                            8
                        );
                        const metricKeys = new Set(
                            metricItems.map((item) => item.key)
                        );
                        const groupedDetailItems = groupedDetails
                            .map(({ group, items }) => ({
                                group,
                                items: items.filter(
                                    (item) => !metricKeys.has(item.key)
                                )
                            }))
                            .filter(({ items }) => items.length > 0);

                        const getTileDisplay = (item: DetailItem) => {
                            if (isRangeKey(item.key)) {
                                const rangeValue = formatVisitDateRange(
                                    item.rawValue,
                                    i18n.currentLocale
                                );

                                return {
                                    value:
                                        rangeValue?.dateLabel ?? 'Not provided',
                                    unit: undefined,
                                    helper: rangeValue?.timeLabel ?? undefined
                                };
                            }

                            const dateTimeValue = formatDateTimeValue(
                                item.rawValue,
                                i18n.currentLocale
                            );

                            if (dateTimeValue) {
                                return {
                                    value: dateTimeValue.dateLabel,
                                    unit: undefined,
                                    helper: dateTimeValue.timeLabel ?? undefined
                                };
                            }

                            return getMetricDisplay(item.key, item.rawValue);
                        };

                        const renderTile = (
                            item: DetailItem,
                            size: 'lg' | 'md'
                        ) => {
                            const display = getTileDisplay(item);
                            return (
                                <MetricTile
                                    key={`${size}-${item.key}`}
                                    label={formatMetricLabel(item.key)}
                                    value={display.value}
                                    unit={display.unit}
                                    helper={
                                        'helper' in display
                                            ? display.helper
                                            : undefined
                                    }
                                    size={size}
                                    icon={getMetricIcon(item.key)}
                                    iconClassName={getMetricIconTone(item.key)}
                                    allowValueWrap={size === 'md'}
                                />
                            );
                        };

                        const downloadGpx = () => {
                            const url =
                                fitnessActivityDetails.gpxFileUrl as string;
                            const link = document.createElement('a');
                            link.href = url;
                            // Let server filename prevail if provided via Content-Disposition
                            link.download = 'activity.gpx';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        };

                        return (
                            <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
                                <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                                    <MetricTile
                                        label="Visibility in app"
                                        value={visibilityValue}
                                        tone="neutral"
                                        size="md"
                                        reserveHelperSpace
                                    />
                                    {reasonLabel && (
                                        <MetricTile
                                            label="Reason / Notes"
                                            value={reasonLabel}
                                            tone="error"
                                            size="md"
                                            icon={Info}
                                            iconClassName="text-destructive"
                                            reserveHelperSpace
                                        />
                                    )}
                                </div>
                                {workoutTypeTiles.length > 0 && (
                                    <div>
                                        <SectionHeader label="Workout Type" />
                                        <div className="mt-3 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                                            {workoutTypeTiles.map((tile) => (
                                                <MetricTile
                                                    key={`workout-type-${tile.key}`}
                                                    label={tile.label}
                                                    value={tile.value}
                                                    size="md"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {metricItems.length > 0 && (
                                    <div>
                                        <SectionHeader label="Activity Metrics" />
                                        <div className={TILE_GRID}>
                                            {metricItems.map((item) =>
                                                renderTile(item, 'lg')
                                            )}
                                        </div>
                                    </div>
                                )}
                                {groupedDetailItems.map(({ group, items }) => (
                                    <div key={`group-${group}`}>
                                        <SectionHeader label={group} />
                                        <div className={TILE_GRID}>
                                            {items.map((item) =>
                                                renderTile(item, 'md')
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="flex flex-wrap gap-2">
                                    {showBackfillButton && (
                                        <Button
                                            variant="outline"
                                            onClick={async () => {
                                                await backfillRequest(
                                                    fitnessActivityId
                                                );
                                                activityDetailsRequest.request([
                                                    fitnessActivityId
                                                ]);
                                            }}
                                        >
                                            Backfill
                                        </Button>
                                    )}
                                    {fitnessActivityDetails.rejectionReason ===
                                        null && (
                                        <Button
                                            variant="outline"
                                            onClick={async () => {
                                                await rejectRequest(
                                                    fitnessActivityId
                                                );
                                                activityDetailsRequest.request([
                                                    fitnessActivityId
                                                ]);
                                            }}
                                        >
                                            Reject
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            navigate(
                                                links.userAnalytics(
                                                    user.id.toString()
                                                ),
                                                {
                                                    state: {
                                                        date: new Date(
                                                            fitnessActivityDetails.createdDate
                                                        )
                                                    }
                                                }
                                            );
                                        }}
                                    >
                                        View in Fraud Monitor
                                    </Button>
                                    {fitnessActivityDetails.rejectionReason !==
                                        null && (
                                        <Button
                                            variant="outline"
                                            onClick={async () => {
                                                await approveRequest(
                                                    fitnessActivityId
                                                );
                                                activityDetailsRequest.request([
                                                    fitnessActivityId
                                                ]);
                                            }}
                                        >
                                            Approve
                                        </Button>
                                    )}
                                    {fitnessActivityDetails.gpxFileUrl && (
                                        <Button
                                            variant="outline"
                                            onClick={downloadGpx}
                                        >
                                            Download GPX
                                        </Button>
                                    )}
                                </div>
                                {fitnessActivityDetails.gpxAnalysis && (
                                    <GpxAnalysisPanel
                                        analysis={
                                            fitnessActivityDetails.gpxAnalysis
                                        }
                                    />
                                )}
                                {fitnessActivityDetails.backfill
                                    ?.backfillDateTime && (
                                    <div className="flex flex-col gap-1">
                                        <Label>Last BackFill Date</Label>
                                        <span className="text-sm">
                                            {formatDateTimeLocal(
                                                fitnessActivityDetails.backfill
                                                    .backfillDateTime,
                                                i18n.currentLocale
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    }
                })}
            </>
        );
    }
);
